import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { WishlistButton } from "@/components/WishlistButton";
import { useCart } from "@/lib/cart";

export type ProductCardItem = {
  id: string;
  slug: string;
  title: string;
  price: number | string | null;
  sale_price?: number | string | null;
  featured_image_url: string | null;
  vendor: { store_name: string; slug: string | null } | null;
};

export function ProductCard({ product, onUnavailable }: { product: ProductCardItem; onUnavailable?: (id: string) => void }) {
  const regular = product.price != null ? Number(product.price) : null;
  const sale = product.sale_price != null ? Number(product.sale_price) : null;
  const hasSale = sale != null && regular != null && sale < regular;
  const price = hasSale ? sale : regular;
  const { add } = useCart();
  const [imgFailed, setImgFailed] = useState(false);

  const hasImage = Boolean(product.featured_image_url) && !imgFailed;
  useEffect(() => {
    if (!hasImage) onUnavailable?.(product.id);
  }, [hasImage, product.id, onUnavailable]);





  const handleAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (price == null) return;
    add({
      id: product.id,
      slug: product.slug,
      title: product.title,
      price,
      image: product.featured_image_url ?? "",
      category: "",
      seller: product.vendor?.store_name ?? "",
      sellerSlug: product.vendor?.slug ?? "",
      description: "",
      stock: 0,
    });
  };

  return (
    <div className="group relative min-w-0 text-center">
      <WishlistButton productId={product.id} className="absolute top-3 right-3 z-10" />
      <Link to="/izdelki/$slug" params={{ slug: product.slug }} className="block">
        <div className="transition-all duration-300 group-hover:ring-2 group-hover:ring-primary group-hover:ring-offset-2">
          <div className="relative aspect-square overflow-hidden bg-muted">
            {hasImage ? (
              <img
                src={product.featured_image_url!}
                alt={product.vendor ? `${product.title} – izdelek ponudnika ${product.vendor.store_name}` : product.title}
                width={600}
                height={600}
                sizes="(max-width: 767px) 46vw, (max-width: 1023px) 30vw, 25vw"
                loading="lazy"
                decoding="async"
                onError={() => setImgFailed(true)}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted px-3 text-center">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  Fotografija v pripravi
                </span>
              </div>
            )}

            <div className="absolute inset-0 flex items-start justify-center pt-4 bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              {price != null && (
                <button
                  type="button"
                  onClick={handleAdd}
                  className="bg-primary text-primary-foreground px-4 py-2 text-xs tracking-wide hover:opacity-90"
                >
                  Dodaj v košarico
                </button>
              )}
            </div>
          </div>
          <div className="mt-2 flex min-w-0 flex-col items-center gap-0.5 pb-2 md:mt-3">
            <h3 className="min-h-[3.3rem] w-full overflow-hidden px-1 font-serif text-sm leading-tight group-hover:text-accent transition-colors line-clamp-3 uppercase md:min-h-[4.25rem] md:px-2 md:text-lg">
              {product.title}
            </h3>
            <div className="max-w-full truncate text-xs text-brand md:text-sm">
              {product.vendor?.store_name ?? ""}
            </div>
            <div className="font-serif text-base font-bold text-accent md:text-xl">
              {price != null ? (
                hasSale ? (
                  <>
                    <span className="mr-2 text-sm font-normal text-muted-foreground line-through md:text-base">
                      €{regular!.toFixed(2)}
                    </span>
                    €{price.toFixed(2)}
                  </>
                ) : (
                  `€${price.toFixed(2)}`
                )
              ) : ""}
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
